import React, { useState, useEffect } from 'react';
import { Box, IconButton, Tooltip, Button } from '@mui/material';
import { Add, Refresh } from '@mui/icons-material';
import { Member } from '@/api/bol';
import { PageLayout, DataGridContainer, JsonDetailPanel } from '@/components';
import { useDataGridSelection, usePagination, useSnackbarNotification, usePermissions, getActionTooltip } from '@/hooks';
import { createMemberColumns } from '@/components/bol/management/members/MemberColumns';
import { MemberFormDialog } from '@/components/bol/management/members/MemberFormDialog';
import { MemberDeleteDialog } from '@/components/bol/management/members/MemberDeleteDialog';
import { useMembersManagement } from '@/hooks/useMembersManagement';

const MembersPage: React.FC = () => {
  const { can } = usePermissions();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Use custom hooks for member management
  const {
    members,
    loading,
    selectedMember,
    setSelectedMember,
    fetchMembers,
    createMember,
    deleteMember,
    total
  } = useMembersManagement();

  // Use custom hooks for common functionality
  const { 
    selectedItem: selectedMemberForDetail, 
    selectedRowId,
    handleRowSelectionChange,
    handleCloseDetailPanel 
  } = useDataGridSelection<Member>({ 
    data: members, 
    getItemId: (member) => member.identifier 
  });

  const {
    pagination,
    handlePaginationModelChange,
  } = usePagination({
    onPageChange: (page, pageSize) => {
      void fetchMembers(page, pageSize);
    }
  });

  const {
    snackbarOpen,
    snackbarMessage,
    snackbarType,
    closeSnackbar
  } = useSnackbarNotification();

  // Event handlers
  const handleCreateMember = async (memberData: { identifier: string }) => {
    await createMember(memberData);
    setCreateDialogOpen(false);
    fetchMembers(pagination.page, pagination.pageSize);
  };

  const handleDeleteMember = (member: Member) => {
    setSelectedMember(member);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async (identifier: string) => {
    await deleteMember(identifier);
    setSelectedMember(null);
    setDeleteDialogOpen(false);
    fetchMembers(pagination.page, pagination.pageSize);
  };

  const handleRefresh = () => {
    fetchMembers(pagination.page, pagination.pageSize);
  };

  // Create columns configuration
  const columns = createMemberColumns({
    onDelete: handleDeleteMember,
    canDelete: can.deleteMember,
    deleteTooltip: getActionTooltip('deleteMember')
  });

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  return (
    <PageLayout 
      title="Members Management"
      selectedItemInfo={selectedMemberForDetail ? `Selected: ${selectedMemberForDetail.identifier}` : undefined}
      notificationOpen={snackbarOpen}
      notificationMessage={snackbarMessage}
      notificationType={snackbarType}
      onNotificationClose={closeSnackbar}
      leftTitleActions={
        <Tooltip title="Refresh members list">
          <IconButton
            onClick={handleRefresh}
            size="small"
          >
            <Refresh />
          </IconButton>
        </Tooltip>
      }
      titleActions={
        <Tooltip title={!can.createMember ? getActionTooltip('createMember') : ''}>
          <span>
            <Button
              variant="contained"
              size="medium"
              onClick={() => setCreateDialogOpen(true)}
              startIcon={<Add />}
              disabled={!can.createMember}
            >
              Create Member
            </Button>
          </span>
        </Tooltip>
      }
      sidePanel={selectedMemberForDetail ? (
        <JsonDetailPanel
          title="Member Details"
          data={selectedMemberForDetail}
          onClose={handleCloseDetailPanel}
          onCopyError={() => {}} // Error handling moved to hook
        />
      ) : undefined}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <DataGridContainer
          rows={members}
          columns={columns}
          loading={loading}
          selectedRowId={selectedRowId}
          onRowSelectionChange={handleRowSelectionChange}
          paginationModel={pagination}
          rowCount={total}
          onPaginationModelChange={handlePaginationModelChange}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          getRowId={(row: { identifier: any; }) => row.identifier}
          columnOrderStorageKey="members-columns"
        />
      </Box>

      {/* Create Member Dialog */}
      <MemberFormDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateMember}
      />

      {/* Delete Confirmation Dialog */}
      <MemberDeleteDialog
        open={deleteDialogOpen}
        member={selectedMember}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedMember(null);
        }}
        onDelete={handleConfirmDelete}
      />
    </PageLayout>
  );
};

export default MembersPage;
